import React, { useEffect, useState, useRef } from 'react';
import { UserProfile, GeneratedImage, CreditTransaction } from '../types';
import { 
  LogOut, CreditCard, Sparkles, X, Mail, Loader2, 
  Globe, Package, Zap, Check, AlertCircle, AlertTriangle,
  Image as ImageIcon, Settings, Clock, Download, 
  LayoutDashboard, CloudUpload, History, Receipt,
  Eye, ShoppingBag, CheckCircle2, CheckCircle, RefreshCw, ShieldAlert, Trash2, Edit3,
  Maximize2, ChevronLeft, ChevronRight, Archive, ClipboardList
} from 'lucide-react';
import { ACCEPTED_IMAGE_TYPES, MAX_FILE_SIZE_MB } from '../constants';
import { getUserImages, getUserTransactions, supabase, isConfigured, redeemPromoCode, getThumbnailUrl as getSupabaseThumbnailUrl } from '../services/supabase';
import { formatMoneyFromMinor, normalizeSketchplanCurrency, buildDashboardProductCatalog } from '@rehub/pricing';

// Shared catalogue — amounts and Stripe IDs come from @rehub/pricing.
const PRODUCT_CATALOG = buildDashboardProductCatalog(
  ((import.meta as any).env || {}) as Record<string, string | undefined>
);
import VolumePricingWidget from './VolumePricingWidget';
import OrderWorkspace from './OrderWorkspace';
import JSZip from 'jszip';

/** output_url can be a single URL or a JSON array of URLs. Returns all URLs. */
function getAllOutputUrls(outputUrl: string | null | undefined): string[] {
  if (!outputUrl?.trim()) return [];
  const trimmed = outputUrl.trim();
  if (trimmed.startsWith('[')) {
    try {
      const arr = JSON.parse(trimmed) as unknown;
      if (Array.isArray(arr)) return arr.filter((u): u is string => typeof u === 'string');
    } catch { /* fallback */ }
  }
  return [trimmed];
}

interface DashboardProps {
  user: UserProfile;
  onClose: () => void;
  onLogout: () => void;
  onOpenEditor: (file: File) => void;
  onConsumeCredit: () => Promise<'success' | 'insufficient_credits' | 'error'>;
  onContinueEditing: (file: File) => void;
  currentApp?: 'photoclear' | 'staging' | 'sketchplan'; // Which app is hosting this dashboard
  initialBillingTab?: 'subscription' | 'topup'; // Optionally open directly to top-up tab
  /** Pre-fill "Buy exact credits" amount (Sketchplan) when opening from e.g. Pro order */
  initialCreditsAmount?: number;
}

const minCustomCredits = 5;
const maxCustomCredits = 500;

export default function Dashboard({ user, onClose, onLogout, onOpenEditor, onConsumeCredit, onContinueEditing, currentApp = 'photoclear', initialBillingTab, initialCreditsAmount }: DashboardProps) {
  // If initialBillingTab is provided, open directly to credits section
  const [activeSection, setActiveSection] = useState<'photos' | 'orders' | 'credits' | 'transactions' | 'settings'>(initialBillingTab ? 'credits' : 'photos');
  
  // --- CURRENCY DETECTION LOGIC ---
  const [currency, setCurrency] = useState<string>('USD');

  useEffect(() => {
    // Run once on mount to determine currency
    try {
      const locale = navigator.language;
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (locale === 'en-GB' || timeZone === 'Europe/London') {
        setCurrency('GBP');
      } else {
        setCurrency('USD');
      }
    } catch (e) {
      setCurrency('USD');
    }
  }, []);
  
  const [processingCheckout, setProcessingCheckout] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  // Subscriptions have been removed in favour of automatic monthly volume
  // pricing — the Buy Credits area now only offers one-time top-ups.
  const [billingTab, setBillingTab] = useState<'subscription' | 'topup'>('topup');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [userImages, setUserImages] = useState<GeneratedImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionFilter, setTransactionFilter] = useState<'all' | 'purchase' | 'usage' | 'refund' | 'bonus'>('all');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<GeneratedImage | null>(null);
  const [lightboxShowOriginal, setLightboxShowOriginal] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [galleryPage, setGalleryPage] = useState(0);
const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  
  // Service type filter for gallery - default to current app
  const [serviceFilter, setServiceFilter] = useState<'all' | 'photoclear' | 'staging' | 'sketchplan'>(currentApp);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [redeemingPromo, setRedeemingPromo] = useState(false);
  const [promoMessage, setPromoMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // User orders state
  interface UserOrder {
    id: string;
    created_at: string;
    property_address: string;
    estimated_sq_ft: number;
    property_type: string;
    floors: number;
    turnaround: 'standard' | 'express';
    status: 'awaiting_payment' | 'pending_payment' | 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'quoted' | 'paid';
    output_url: string | null;
    draft_url?: string | null;
    delivered_at: string | null;
    quoted_price: number;
    payment_method?: string | null;
    payment_mode?: string | null;
    amount_pence?: number | null;
    currency?: string | null;
    sketch_url?: string | null;
    notes?: string | null;
    job_number?: string | null;
    amendment_used_at?: string | null;
    size_band?: string | null;
  }
  const [userOrders, setUserOrders] = useState<UserOrder[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  
const IMAGES_PER_PAGE = 12;
  
  // Filter images by service type
  const filteredImages = serviceFilter === 'all' 
    ? userImages 
    : userImages.filter(img => img.service_type === serviceFilter);
  
  const displayedImages = filteredImages.slice(galleryPage * IMAGES_PER_PAGE, (galleryPage + 1) * IMAGES_PER_PAGE);
  const totalPages = Math.ceil(filteredImages.length / IMAGES_PER_PAGE);

  // Handle opening Stripe Customer Portal for subscription management
  const handleManageSubscription = async () => {
    if (!user.stripe_customer_id) {
      alert('No subscription found. Please subscribe first.');
      return;
    }
    
    setLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: {
          customerId: user.stripe_customer_id,
          returnUrl: `${window.location.origin}/dashboard`
        }
      });
      
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      console.error('Portal error:', err);
      alert('Failed to open subscription management. Please try again.');
    } finally {
      setLoadingPortal(false);
    }
  };

  // Check for Payment Success Query Param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
        setPaymentSuccess(true);
        window.history.replaceState({}, '', window.location.pathname);
        // Refresh transactions after a delay to allow webhook to process
        setTimeout(() => {
             if (user.id) {
                 getUserTransactions(user.id).then(setTransactions);
             }
        }, 2000);
        
        // Auto-dismiss success message
        setTimeout(() => setPaymentSuccess(false), 8000);
    }
  }, []);

  // Auto-cleanup stuck processing images (processing for > 5 minutes)
  const cleanupStuckImages = async (images: GeneratedImage[]) => {
    const STUCK_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
    const now = Date.now();
    
    const stuckImages = images.filter(img => {
      if (img.status !== 'processing') return false;
      const createdAt = new Date(img.created_at).getTime();
      return (now - createdAt) > STUCK_THRESHOLD_MS;
    });
    
    if (stuckImages.length > 0) {
      console.log(`Found ${stuckImages.length} stuck processing image(s), marking as failed`);
      
      for (const img of stuckImages) {
        await supabase.from('images').update({ status: 'failed' }).eq('id', img.id);
      }
      
      // Update local state
      setUserImages(prev => prev.map(img => 
        stuckImages.some(s => s.id === img.id) ? { ...img, status: 'failed' } : img
      ));
    }
  };

  useEffect(() => {
    if (user?.id) {
        setLoadingImages(true);
        getUserImages(user.id)
            .then(imgs => {
              setUserImages(imgs);
              // Check for stuck images after loading
              cleanupStuckImages(imgs);
            })
            .catch(err => console.error(err))
            .finally(() => setLoadingImages(false));

        const channel = supabase.channel('dashboard-gallery')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'images', filter: `user_id=eq.${user.id}` }, 
            (payload) => {
                if (payload.eventType === 'INSERT') {
                    setUserImages(prev => [payload.new as GeneratedImage, ...prev]);
                } else if (payload.eventType === 'UPDATE') {
                    setUserImages(prev => prev.map(img => img.id === payload.new.id ? payload.new as GeneratedImage : img));
                }
            })
            .subscribe();

        return () => { supabase.removeChannel(channel); };
    }
  }, [user?.id]);

  useEffect(() => {
    if (activeSection === 'transactions' && user?.id) {
        setLoadingTransactions(true);
        getUserTransactions(user.id)
            .then(data => setTransactions(data))
            .finally(() => setLoadingTransactions(false));
    }
  }, [activeSection, user?.id]);

  // Fetch user floor plan orders - load on mount for badge count
  useEffect(() => {
    if (user?.id) {
      supabase
        .from('pro_orders')
        .select('id, created_at, property_address, estimated_sq_ft, property_type, floors, turnaround, status, output_url, draft_url, delivered_at, quoted_price, payment_method, payment_mode, amount_pence, currency, sketch_url, notes, job_number, amendment_used_at, size_band')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          if (error) {
            console.error('Error fetching orders:', error);
          } else if (data) {
            setUserOrders(data as UserOrder[]);
          }
        });
    }
  }, [user?.id]);

  // Refresh orders when switching to orders tab
  useEffect(() => {
    if (activeSection === 'orders' && user?.id) {
      setLoadingOrders(true);
      supabase
        .from('pro_orders')
        .select('id, created_at, property_address, estimated_sq_ft, property_type, floors, turnaround, status, output_url, draft_url, delivered_at, quoted_price, payment_method, payment_mode, amount_pence, currency, sketch_url, notes, job_number, amendment_used_at, size_band')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          if (error) {
            console.error('Error fetching orders:', error);
          } else if (data) {
            setUserOrders(data as UserOrder[]);
          }
        })
        .finally(() => setLoadingOrders(false));
    }
  }, [activeSection, user?.id]);

  // Calculate estimated completion time for an order
  const getEstimatedCompletion = (createdAt: string, turnaround: 'standard' | 'express'): { text: string; isOverdue: boolean } => {
    const created = new Date(createdAt);
    const now = new Date();
    const hoursToAdd = turnaround === 'express' ? 24 : 48;
    
    // Calculate business hours (skip weekends)
    let targetDate = new Date(created);
    let hoursAdded = 0;
    
    while (hoursAdded < hoursToAdd) {
      targetDate.setHours(targetDate.getHours() + 1);
      const day = targetDate.getDay();
      // Only count hours on weekdays (Mon-Fri = 1-5)
      if (day !== 0 && day !== 6) {
        hoursAdded++;
      }
    }
    
    const diffMs = targetDate.getTime() - now.getTime();
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    
    if (diffMs <= 0) {
      return { text: 'Any moment now', isOverdue: true };
    } else if (diffHours < 1) {
      return { text: 'Less than 1 hour', isOverdue: false };
    } else if (diffHours === 1) {
      return { text: '~1 hour', isOverdue: false };
    } else if (diffHours < 24) {
      return { text: `~${diffHours} hours`, isOverdue: false };
    } else {
      const days = Math.ceil(diffHours / 24);
      return { text: `~${days} day${days > 1 ? 's' : ''}`, isOverdue: false };
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (ACCEPTED_IMAGE_TYPES.includes(file.type) && file.size <= MAX_FILE_SIZE_MB * 1024 * 1024) {
        onOpenEditor(file);
      }
    }
  };

  // Generate thumbnail URL with Supabase image transforms
  const getThumbnailUrl = (url: string, width: number = 300) => {
    if (!url) return url;
    const thumbnailUrl = getSupabaseThumbnailUrl(url, {
      width,
      height: Math.round(width * 0.75),
      quality: 70,
      resize: 'cover',
    });
    return thumbnailUrl || url;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];
        onOpenEditor(file);
    }
    e.target.value = '';
  };

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) {
      setPromoMessage({ type: 'error', text: 'Please enter a promo code' });
      return;
    }

    setRedeemingPromo(true);
    setPromoMessage(null);

    try {
      const result = await redeemPromoCode(promoCode.trim());

      if (result.success) {
        setPromoMessage({ 
          type: 'success', 
          text: `Success! ${result.credits_added} credits added to your account.` 
        });
        setPromoCode('');
        
        // Refresh user data to show updated credits
        if (user.id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('credits')
            .eq('id', user.id)
            .single();
          
          if (profile) {
            user.credits = profile.credits;
          }
          
          // Refresh transactions
          getUserTransactions(user.id).then(setTransactions);
        }
      } else {
        const errorMessages: Record<string, string> = {
          'NOT_AUTHENTICATED': 'You must be logged in to redeem a code',
          'INVALID_CODE': 'Invalid promo code',
          'CODE_INACTIVE': 'This promo code is no longer active',
          'CODE_EXPIRED': 'This promo code has expired',
          'CODE_EXHAUSTED': 'This promo code has reached its usage limit',
          'ALREADY_REDEEMED': 'You have already redeemed this code'
        };
        
        setPromoMessage({ 
          type: 'error', 
          text: errorMessages[result.error || ''] || 'Failed to redeem code' 
        });
      }
    } catch (err) {
      console.error('Redeem promo error:', err);
      setPromoMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setRedeemingPromo(false);
    }
  };

  const handleCheckout = async (credits: number, priceId: string, mode: 'subscription' | 'payment', quantity?: number) => {
    setCheckoutError(null);
    const qty = quantity ?? 1;
    console.log("Initiating checkout:", { credits, priceId, mode, quantity: qty, userId: user?.id });

    if (!isConfigured) {
        setCheckoutError("System Error: Application is not connected to Supabase backend.");
        return;
    }

    if (!user || !user.id) {
        setCheckoutError("System Error: User ID not found.");
        return;
    }
    if (!priceId) {
        setCheckoutError("Product Configuration Error: Missing Price ID.");
        return;
    }
    
    const loadingId = priceId + (qty > 1 ? `_${qty}` : '');
    setProcessingCheckout(loadingId);
    
    try {
      const metadata = { 
          credit_amount: String(credits),
          credits: String(credits),
          user_id: user.id 
      };

      const checkoutBody = {
          priceId: priceId,
          mode: mode, 
          quantity: qty,
          client_reference_id: user.id, 
          userId: user.id,
          successUrl: `https://hub.rehub.studio/#/purchase-complete?app=sketchplan&type=credits&return=${encodeURIComponent(window.location.origin + '/dashboard')}&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/dashboard?canceled=true`,
          metadata: metadata
      };

      // Renamed to 'api-payment' to avoid AdBlockers which block urls containing 'checkout'
      // Add a timeout so checkout never hangs indefinitely on blocked/stalled requests.
      const invokePromise = supabase.functions.invoke('api-payment', {
        body: checkoutBody,
        headers: { 'Content-Type': 'application/json' }
      });
      const timeoutPromise = new Promise<{ data: null; error: { message: string } }>((resolve) => {
        setTimeout(() => {
          resolve({
            data: null,
            error: {
              message: "Request timed out. Your network, browser extension, or firewall may be blocking checkout.",
            },
          });
        }, 15000);
      });
      const { data, error } = (await Promise.race([invokePromise, timeoutPromise])) as Awaited<typeof invokePromise>;

      if (error) {
          console.error("Function Invoke Error Full:", error);
          
          let msg = "Payment Service Error.";
          // Handle specific Supabase "Failed to send request" error which indicates network failure
          // Also explicitly handle 500s which might be adblockers returning empty responses
          if (error.message && (error.message.includes("Failed to send a request") || error.message.includes("fetch failed"))) {
              msg = "Connection Blocked: Your browser or an extension (like AdBlock/uBlock) blocked the payment request. Please disable ad-blockers for this site and try again.";
          } else if (typeof error === 'object' && error !== null) {
              if (error.context && error.context.status === 500) {
                   msg = "Server Configuration Error. (Hint: Missing STRIPE_SECRET_KEY in Edge Function Secrets?)";
              } else {
                   msg = error.message || JSON.stringify(error);
              }
          }
          throw new Error(msg);
      }

      if (data?.error) {
          throw new Error(data.error);
      }
      
      if (data?.url) {
          window.location.href = data.url;
      } else {
          throw new Error("No payment URL received from server.");
      }

    } catch (err: any) {
      console.error("Checkout Exception:", err);
      setCheckoutError(err.message || 'Checkout failed due to an unexpected error.');
    } finally {
      setProcessingCheckout(null);
    }
  };

  // Sketchplan: buy exact credits at full per-credit price (no discount), or use plans/top-ups below for better rates
  const SKETCHPLAN_EXACT_CREDITS = {
    name: 'Buy exact credits',
    description: 'Minimum of 5 credits. Pay only for the credits you need — no pack discount.',
    creditsPerUnit: 1,
    prices: {
      USD: { id: (import.meta as any).env?.VITE_STRIPE_PRICE_PER_CREDIT_USD || 'price_1T1NYIDHlg75FZHH46JYdTMI', amountPerCredit: 1.05 },
      GBP: { id: (import.meta as any).env?.VITE_STRIPE_PRICE_PER_CREDIT_GBP || 'price_1T1NXsDHlg75FZHHmn69RRi7', amountPerCredit: 0.75 }
    }
  };

  const [customCreditsAmount, setCustomCreditsAmount] = useState<number>(() => {
    const v = initialCreditsAmount ?? 25;
    return Math.min(maxCustomCredits, Math.max(minCustomCredits, v));
  });

  const handleDeleteAccount = async () => {
    if (!isConfigured) {
      alert('System not configured');
      return;
    }

    setDeletingAccount(true);
    try {
      const { data, error } = await supabase.functions.invoke('delete-account', {
        body: {},
      });

      if (error) {
        throw new Error(error.message || 'Failed to delete account');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      // Sign out and redirect
      await supabase.auth.signOut();
      window.location.href = '/';
    } catch (err: any) {
      console.error('Delete account error:', err);
      alert(err.message || 'Failed to delete account. Please try again.');
      setDeletingAccount(false);
      setShowDeleteConfirm(false);
    }
  };

  const getDaysRemaining = (dateStr: string) => {
    const date = new Date(dateStr);
    const expires = new Date(date.getTime() + 7 * 24 * 60 * 60 * 1000); 
    const now = new Date();
    const diff = expires.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 3600 * 24)));
  };

  const handleDelete = async (id: string) => {
      if (!confirm('Are you sure you want to delete this image?')) return;
      
      setDeletingId(id);
      try {
        const { error } = await supabase
          .from('images')
          .delete()
          .eq('id', id);
        
        if (error) throw error;
        
        setUserImages(prev => prev.filter(img => img.id !== id));
      } catch (err) {
        console.error('Delete failed:', err);
        alert('Failed to delete image. Please try again.');
      } finally {
        setDeletingId(null);
      }
  };

  const handleDownload = async (imageUrl: string, id: string) => {
      setDownloadingId(id);
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        let ext = 'jpg';
        if (blob.type === 'image/png') ext = 'png';
        else if (blob.type === 'image/webp') ext = 'webp';
        link.download = `VirtualStaging-${Date.now()}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Download failed:", err);
        const link = document.createElement('a');
        link.href = imageUrl;
        link.target = '_blank';
        link.download = `VirtualStaging-${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        setDownloadingId(null);
      }
  };

  const handleContinueEdit = async (imageUrl: string, id: string) => {
      setEditingId(id);
      try {
        // Fetch the image from the URL
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        
        // Convert blob to File object
        const fileName = `staging-edit-${Date.now()}.jpg`;
        const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
        
        // Close dashboard and load image into editor without charging
        onContinueEditing(file);
      } catch (err) {
        console.error("Failed to load image for editing:", err);
        alert('Failed to load image. Please try again.');
      } finally {
        setEditingId(null);
      }
  };

const handleBulkDownload = async () => {
      // Use filtered images (respects current service filter)
      const completedImages = filteredImages.filter(img => img.status === 'completed' && img.output_url);
      
      if (completedImages.length === 0) {
        alert('No completed images to download.');
        return;
      }
      
      const filterLabel = serviceFilter === 'all' ? '' : serviceFilter === 'photoclear' ? ' Photoclear' : ' Virtual Staging';
      if (!confirm(`Download all ${completedImages.length}${filterLabel} completed images as a ZIP file? This may take a moment.`)) {
        return;
      }
      
      setBulkDownloading(true);
      setBulkProgress(0);
      
      try {
        const zip = new JSZip();
        
        // Use appropriate prefix based on filter
        const prefix = serviceFilter === 'photoclear' ? 'Photoclear' : serviceFilter === 'staging' ? 'VirtualStaging' : 'RehubStudio';
        
        for (let i = 0; i < completedImages.length; i++) {
          const img = completedImages[i];
          try {
            const response = await fetch(img.output_url);
            const blob = await response.blob();
            
            // Determine file extension
            let ext = 'jpg';
            if (blob.type === 'image/png') ext = 'png';
            else if (blob.type === 'image/webp') ext = 'webp';
            
            // Add to ZIP with sequential numbering
            zip.file(`${prefix}-${String(i + 1).padStart(4, '0')}.${ext}`, blob);
          } catch (err) {
            console.error(`Failed to fetch image ${i + 1}:`, err);
          }
          
          setBulkProgress(Math.round(((i + 1) / completedImages.length) * 90));
        }
        
        // Generate ZIP file
        setBulkProgress(95);
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        
        // Download ZIP with appropriate filename
        const url = window.URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${prefix}-Images-${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        setBulkProgress(100);
      } catch (err) {
        console.error('Bulk download failed:', err);
        alert('Failed to create ZIP file. Please try again.');
      } finally {
        setBulkDownloading(false);
        setBulkProgress(0);
      }
  };

  // --- GET DISPLAY PRODUCTS ---
  const getDisplayProducts = (type: 'subscription' | 'topup') => {
    try {
        const list = type === 'subscription' ? PRODUCT_CATALOG.SUBSCRIPTIONS : PRODUCT_CATALOG.TOPUPS;
        if (!list || list.length === 0) return [];
        
        return list.map(product => {
            const priceMap = product.prices as any;
            const priceData = priceMap[currency] || priceMap['USD'] || Object.values(priceMap)[0];
            
            if (!priceData) return null;

            // Handle costPerCredit - can be string (subscriptions) or object (topups)
            let costPerCreditText = null;
            const costPerCreditData = (product as any).costPerCredit;
            if (costPerCreditData) {
                if (typeof costPerCreditData === 'string') {
                    costPerCreditText = costPerCreditData;
                } else if (typeof costPerCreditData === 'object') {
                    costPerCreditText = costPerCreditData[currency] || costPerCreditData['USD'];
                }
            }

                return {
                id: product.id,
                name: product.name,
                credits: product.credits,
                bonus: product.bonus,
                popular: product.popular,
                displayAmount: (priceData as any).amount.toFixed(2),
                priceId: (priceData as any).id,
                currency: currency,
                costPerCredit: costPerCreditText,
                valueLabel: (product as any).valueLabel || null,
                thirdFeature: (product as any).thirdFeature || null
            };
        }).filter(p => p !== null);
    } catch (e) {
        console.error("Error generating products", e);
        return [];
    }
  };

  const displayProducts = getDisplayProducts(billingTab);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-black border border-slate-800 rounded-3xl w-full max-w-6xl shadow-2xl overflow-hidden flex h-[90vh] relative">
        
        {/* --- PAYMENT SUCCESS NOTIFICATION --- */}
        {paymentSuccess && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top-4 duration-300">
                <div className="bg-green-600 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-3 font-semibold border border-green-400/50">
                    <CheckCircle2 className="animate-bounce" size={24} />
                    <span>Payment Successful! Credits have been added to your account.</span>
                    <button onClick={() => setPaymentSuccess(false)} className="ml-2 hover:bg-green-700/50 p-1 rounded-full"><X size={16} /></button>
                </div>
            </div>
        )}

        {/* --- SIDEBAR --- */}
        <div className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col hidden md:flex">
            <div className="p-6">
                <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
                    <LayoutDashboard className="text-teal-500" size={24} />
                    <span>Dashboard</span>
                </h2>
            </div>
            
            <nav className="flex-1 px-4 space-y-2">
                <button onClick={() => setActiveSection('photos')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeSection === 'photos' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/20' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`}>
                    <ImageIcon size={20} /> <span className="font-medium">My Photos</span>
                </button>
                <button onClick={() => setActiveSection('orders')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeSection === 'orders' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/20' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`}>
                    <ClipboardList size={20} /> <span className="font-medium">My Orders</span>
                    {userOrders.filter(o => o.status === 'pending' || o.status === 'in_progress').length > 0 && (
                      <span className="ml-auto bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                        {userOrders.filter(o => o.status === 'pending' || o.status === 'in_progress').length}
                      </span>
                    )}
                </button>
                <button onClick={() => setActiveSection('credits')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeSection === 'credits' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/20' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`}>
                    <CreditCard size={20} /> <span className="font-medium">Buy Credits</span>
                </button>
                <button onClick={() => setActiveSection('transactions')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeSection === 'transactions' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/20' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`}>
                    <Receipt size={20} /> <span className="font-medium">History</span>
                </button>
                <button onClick={() => setActiveSection('settings')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeSection === 'settings' ? 'bg-teal-600 text-white shadow-lg shadow-teal-900/20' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`}>
                    <Settings size={20} /> <span className="font-medium">Settings</span>
                </button>
            </nav>

            <div className="p-4 border-t border-slate-800">
                <button onClick={onClose} className="w-full flex items-center gap-2 text-slate-500 hover:text-white justify-center py-2 transition-colors mb-4">
                    <X size={16} /> Close Dashboard
                </button>
                <div className="flex justify-center mb-4">
                    <img 
                        src="/cropped-Sketchplan-Logo1-removebg-preview.webp"
                        alt="Sketchplan" 
                        className="h-16 w-auto object-contain"
                    />
                </div>
                <div className="bg-black rounded-xl p-4 border border-slate-800">
                     <div className="flex items-center gap-3 mb-3">
                         <div className="w-10 h-10 bg-teal-900/50 rounded-full flex items-center justify-center text-teal-400 font-bold border border-teal-800">
                             {user.email.charAt(0).toUpperCase()}
                         </div>
                         <div className="overflow-hidden">
                             <p className="text-sm font-semibold text-white truncate">{user.email.split('@')[0]}</p>
                             <p className="text-xs text-slate-500 truncate">{user.email}</p>
                         </div>
                     </div>
                     <button onClick={onLogout} className="w-full flex items-center justify-center gap-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/10 py-2 rounded-lg transition-colors">
                         <LogOut size={14} /> Sign Out
                     </button>
                </div>
            </div>
        </div>

        {/* --- MAIN CONTENT --- */}
        <div className="flex-1 flex flex-col bg-black overflow-hidden relative">
            <header className="h-14 sm:h-16 border-b border-slate-800 flex items-center justify-between px-3 sm:px-6 bg-slate-900/50 backdrop-blur-sm z-10 shrink-0">
                <div className="min-w-0">
                   <h1 className="text-base sm:text-xl font-bold text-white truncate">
                       {activeSection === 'photos' && 'Gallery'}
                       {activeSection === 'orders' && 'My Orders'}
                       {activeSection === 'credits' && 'Credits'}
                       {activeSection === 'transactions' && 'History'}
                       {activeSection === 'settings' && 'Settings'}
                   </h1>
                </div>
                <div className="flex items-center gap-2 sm:gap-4">
                    {/* Mobile credits badge */}
                    <div className="flex sm:hidden bg-teal-900/20 border border-orange-900/50 px-2.5 py-1.5 rounded-full items-center gap-1.5">
                        <Zap size={12} className="text-teal-400 fill-teal-400" />
                        <span className="font-bold text-teal-200 text-xs">{user.credits}</span>
                    </div>
                    {/* Plan Badge with Upgrade/Manage */}
                    <div className="hidden sm:flex bg-gradient-to-r from-teal-900/30 to-indigo-900/30 border border-teal-500/30 px-3 py-1.5 rounded-full items-center gap-2">
                        <Package size={14} className="text-teal-400" />
                        <span className="font-semibold text-teal-200 text-sm">{user.subscription_plan || 'Free'}</span>
                        {user.subscription_plan && user.stripe_customer_id ? (
                            <button 
                                onClick={handleManageSubscription}
                                disabled={loadingPortal}
                                className="ml-1 bg-teal-600 hover:bg-teal-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-md transition-colors disabled:opacity-50"
                            >
                                {loadingPortal ? '...' : 'Manage'}
                            </button>
                        ) : (
                            <button 
                                onClick={() => setActiveSection('credits')}
                                className="ml-1 bg-teal-600 hover:bg-teal-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-md transition-colors"
                            >
                                Upgrade
                            </button>
                        )}
                    </div>
                    <div className="hidden sm:flex bg-teal-900/20 border border-orange-900/50 px-4 py-2 rounded-full items-center gap-2 shadow-[0_0_15px_rgba(249,115,22,0.15)]">
                        <Zap size={16} className="text-teal-400 fill-teal-400" />
                        <span className="font-bold text-teal-200">{user.credits} Credits</span>
                        <button onClick={() => setActiveSection('credits')} className="ml-2 bg-teal-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-md hover:bg-teal-400 transition-colors">ADD</button>
                    </div>
                    <button onClick={onClose} className="md:hidden min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 touch-manipulation" aria-label="Close"><X size={22} /></button>
                </div>
            </header>

            {/* Mobile Navigation - touch-friendly min 44px */}
            <nav className="md:hidden flex items-center justify-between gap-2 px-3 py-2 bg-slate-900 border-b border-slate-800 shrink-0 overflow-x-auto">
                <div className="flex items-center gap-1 min-h-[44px]">
                    <button 
                        onClick={() => setActiveSection('photos')} 
                        className={`flex items-center gap-1.5 px-3 py-2.5 min-h-[44px] rounded-xl text-xs font-medium transition-all touch-manipulation ${activeSection === 'photos' ? 'bg-teal-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
                    >
                        <ImageIcon size={16} /> Photos
                    </button>
                    <button 
                        onClick={() => setActiveSection('orders')} 
                        className={`relative flex items-center gap-1.5 px-3 py-2.5 min-h-[44px] rounded-xl text-xs font-medium transition-all touch-manipulation ${activeSection === 'orders' ? 'bg-teal-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
                    >
                        <ClipboardList size={16} /> Orders
                        {userOrders.filter(o => o.status === 'pending' || o.status === 'in_progress').length > 0 && (
                          <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center animate-pulse px-1">
                            {userOrders.filter(o => o.status === 'pending' || o.status === 'in_progress').length}
                          </span>
                        )}
                    </button>
                    <button 
                        onClick={() => setActiveSection('credits')} 
                        className={`flex items-center gap-1.5 px-3 py-2.5 min-h-[44px] rounded-xl text-xs font-medium transition-all touch-manipulation ${activeSection === 'credits' ? 'bg-teal-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
                    >
                        <CreditCard size={16} /> Buy
                    </button>
                    <button 
                        onClick={() => setActiveSection('transactions')} 
                        className={`flex items-center gap-1.5 px-3 py-2.5 min-h-[44px] rounded-xl text-xs font-medium transition-all touch-manipulation ${activeSection === 'transactions' ? 'bg-teal-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
                    >
                        <Receipt size={16} /> History
                    </button>
                    <button 
                        onClick={() => setActiveSection('settings')} 
                        className={`flex items-center gap-1.5 px-3 py-2.5 min-h-[44px] rounded-xl text-xs font-medium transition-all touch-manipulation ${activeSection === 'settings' ? 'bg-teal-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
                    >
                        <Settings size={16} /> Settings
                    </button>
                </div>
                <button 
                    onClick={onLogout} 
                    className="flex items-center gap-1.5 px-3 py-2.5 min-h-[44px] rounded-xl text-xs font-medium text-red-400 hover:bg-red-900/20 transition-all touch-manipulation shrink-0"
                >
                    <LogOut size={16} /> Out
                </button>
            </nav>

            <main className="flex-1 overflow-y-auto custom-scrollbar p-8">
                {/* PHOTOS */}
                {activeSection === 'photos' && (
                    <div className="space-y-8 animate-fade-in">
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
                            <div className="flex items-center justify-between mb-4 relative z-10">
                                <h3 className="font-semibold text-slate-200 flex items-center gap-2"><Sparkles size={18} className="text-teal-400" /> New Restoration</h3>
                            </div>
                            <div 
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
                                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
                                onDrop={handleFileDrop}
                                onClick={() => fileInputRef.current?.click()}
                                className={`border-2 border-dashed rounded-xl h-40 flex flex-col items-center justify-center gap-3 transition-all cursor-pointer group relative z-10 ${isDragOver ? 'border-teal-500 bg-teal-900/20' : 'border-slate-700 bg-black hover:border-slate-600 hover:bg-slate-900'}`}
                            >
                                <input type="file" ref={fileInputRef} className="sr-only" accept={ACCEPTED_IMAGE_TYPES.join(',')} onChange={handleFileSelect} />
                                <div className="p-3 bg-slate-800 rounded-full group-hover:bg-teal-600 group-hover:text-white transition-colors text-slate-400 shadow-lg pointer-events-none"><CloudUpload size={28} /></div>
                                <div className="text-center pointer-events-none">
                                    <p className="text-slate-200 font-medium text-lg">Click or Drag to Start</p>
                                    <p className="text-slate-500 text-sm">Uses 6 credits per image</p>
                                </div>
                            </div>
                        </div>

<div>
                            <div className="flex flex-col gap-4 mb-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="font-semibold text-slate-200 flex items-center gap-2"><History size={18} className="text-slate-400" /> Recent Activity</h3>
                                    <p className="text-sm text-white font-medium">Images are stored for 30 days</p>
                                </div>
                                
                                {/* Service Type Filter Tabs */}
                                <div className="flex items-center justify-between">
                                    <div className="bg-slate-900/50 p-1 rounded-xl border border-slate-800 flex items-center gap-1">
                                        <button
                                            onClick={() => { setServiceFilter('all'); setGalleryPage(0); }}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${serviceFilter === 'all' ? 'bg-teal-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                                        >
                                            All ({userImages.length})
                                        </button>
                                        <button
                                            onClick={() => { setServiceFilter('photoclear'); setGalleryPage(0); }}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${serviceFilter === 'photoclear' ? 'bg-teal-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                                        >
                                            Photoclear ({userImages.filter(img => img.service_type === 'photoclear').length})
                                        </button>
                                        <button
                                            onClick={() => { setServiceFilter('staging'); setGalleryPage(0); }}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${serviceFilter === 'staging' ? 'bg-teal-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                                        >
                                            Virtual Staging ({userImages.filter(img => img.service_type === 'staging').length})
                                        </button>
                                        <button
                                            onClick={() => { setServiceFilter('sketchplan'); setGalleryPage(0); }}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${serviceFilter === 'sketchplan' ? 'bg-teal-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                                        >
                                            Sketchplan ({userImages.filter(img => img.service_type === 'sketchplan').length})
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-4">
                                    {filteredImages.filter(img => img.status === 'completed' && img.output_url).length > 0 && (
                                        <button
                                            onClick={handleBulkDownload}
                                            disabled={bulkDownloading}
                                            className="px-4 py-2 text-sm bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white rounded-lg flex items-center gap-2 font-medium shadow-lg shadow-green-900/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {bulkDownloading ? (
                                                <>
                                                    <Loader2 size={16} className="animate-spin" />
                                                    <span>Downloading {bulkProgress}%</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Archive size={16} />
                                                    <span>Download All ({filteredImages.filter(img => img.status === 'completed' && img.output_url).length})</span>
                                                </>
                                            )}
                                        </button>
                                    )}
                                    {totalPages > 1 && (
                                        <div className="flex items-center gap-2">
                                            <button 
                                                onClick={() => setGalleryPage(prev => Math.max(0, prev - 1))}
                                                disabled={galleryPage === 0}
                                                className="px-3 py-1 text-xs bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                Previous
                                            </button>
                                            <span className="text-xs text-slate-400">
                                                {galleryPage + 1} / {totalPages}
                                            </span>
                                            <button 
                                                onClick={() => setGalleryPage(prev => Math.min(totalPages - 1, prev + 1))}
                                                disabled={galleryPage === totalPages - 1}
                                                className="px-3 py-1 text-xs bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    )}
                                    </div>
                                </div>
                            </div>
                            {loadingImages ? (
                                <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-teal-500 animate-spin" /></div>
                            ) : filteredImages.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {displayedImages.map((img) => (
                                        <div key={img.id} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden group hover:border-slate-700 transition-colors shadow-lg">
                                            <div className="relative aspect-[4/3] overflow-hidden group/image bg-black">
                                                <img src={getThumbnailUrl(img.input_url)} alt="Original" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                                                {img.status === 'completed' && img.output_url && (
                                                    <img src={getThumbnailUrl(img.output_url)} alt="Restored" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300 opacity-100 group-hover/image:opacity-0" />
                                                )}
                                                {img.status === 'processing' && (
                                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                                        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                                                    </div>
                                                )}
                                                {img.status === 'failed' && (
                                                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                                                        <div className="text-center">
                                                            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                                                            <span className="text-xs text-red-300">Failed</span>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="absolute top-2 left-2 pointer-events-none z-20">
                                                    <span className="bg-black/60 text-white text-[10px] font-bold px-2 py-1 rounded backdrop-blur-md border border-white/10 opacity-0 group-hover/image:opacity-100 transition-opacity flex items-center gap-1"><Eye size={10} /> Original</span>
                                                </div>
                                            </div>
                                            <div className="p-3 flex items-center justify-between bg-slate-900 border-t border-slate-800">
                                                <div className="flex items-center gap-2">
                                                    {img.status === 'completed' && (
                                                        <div className="flex items-center gap-1.5 text-green-400 bg-green-900/20 px-2 py-0.5 rounded text-xs font-medium border border-green-900/50"><Check size={10} /> Ready</div>
                                                    )}
                                                    {img.status === 'processing' && (
                                                         <div className="flex items-center gap-1.5 text-teal-400 bg-teal-900/20 px-2 py-0.5 rounded text-xs font-medium border border-orange-900/50"><Loader2 size={10} className="animate-spin" /> Processing</div>
                                                    )}
                                                    {img.status === 'failed' && (
                                                         <div className="flex items-center gap-1.5 text-red-400 bg-red-900/20 px-2 py-0.5 rounded text-xs font-medium border border-red-900/50"><AlertCircle size={10} /> Failed</div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {img.status === 'completed' && (
                                                        <button onClick={() => { setLightboxImage(img); setLightboxShowOriginal(false); }} className="text-xs bg-blue-600/10 text-blue-400 hover:bg-blue-600 hover:text-white p-1.5 rounded-lg flex items-center transition-all border border-blue-600/20 hover:border-blue-500" title="View Full Size">
                                                            <Maximize2 size={12} />
                                                        </button>
                                                    )}
                                                    <button onClick={() => handleContinueEdit(img.output_url, img.id)} disabled={editingId === img.id || img.status !== 'completed'} className="text-xs bg-teal-600/10 text-teal-400 hover:bg-teal-600 hover:text-white p-1.5 rounded-lg flex items-center transition-all border border-teal-600/20 hover:border-teal-500 disabled:opacity-50" title="Continue Editing">
                                                        {editingId === img.id ? <Loader2 size={12} className="animate-spin" /> : <Edit3 size={12} />}
                                                    </button>
                                                    <button onClick={() => handleDownload(img.output_url, img.id)} disabled={downloadingId === img.id || img.status !== 'completed'} className="text-xs bg-slate-700/50 text-slate-300 hover:bg-slate-600 hover:text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all font-medium border border-slate-600/30 hover:border-slate-500 disabled:opacity-50">
                                                        {downloadingId === img.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                                                    </button>
                                                    <button onClick={() => handleDelete(img.id)} disabled={deletingId === img.id} className="text-xs bg-red-600/10 text-red-400 hover:bg-red-600 hover:text-white p-1.5 rounded-lg flex items-center transition-all border border-red-600/20 hover:border-red-500 disabled:opacity-50">
                                                        {deletingId === img.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-16 border border-slate-800 rounded-2xl bg-slate-900/50 border-dashed">
                                    <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-600"><ImageIcon size={32} /></div>
                                    <h4 className="text-slate-300 font-medium mb-1">
                                        {serviceFilter === 'all' ? 'No images yet' : 
                                         serviceFilter === 'photoclear' ? 'No Photoclear images' : 
                                         'No Virtual Staging images'}
                                    </h4>
                                    <p className="text-sm text-slate-500 max-w-xs mx-auto mb-6">
                                        {serviceFilter === 'all' ? 'Your generated images will appear here.' : 
                                         `No images from ${serviceFilter === 'photoclear' ? 'Photoclear' : 'Virtual Staging'} found. Try selecting "All" to see all your images.`}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ORDERS SECTION */}
                {activeSection === 'orders' && (
                    <div className="max-w-4xl mx-auto animate-fade-in space-y-6">
                        {/* Volume pricing summary */}
                        <VolumePricingWidget customPlanRatePence={user.custom_plan_rate_pence ?? null} />

                        {/* Header info */}
                        <div className="bg-gradient-to-r from-amber-900/20 to-orange-900/20 border border-amber-500/30 rounded-2xl p-5">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-amber-500/20 rounded-xl">
                                    <Clock size={24} className="text-amber-400" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-white mb-1">Turnaround Times</h3>
                                    <p className="text-slate-400 text-sm leading-relaxed">
                                        <strong className="text-amber-400">Standard:</strong> 48 hours • <strong className="text-teal-400">Express:</strong> 24 hours
                                    </p>
                                    <p className="text-slate-500 text-xs mt-2 flex items-center gap-1.5">
                                        <AlertTriangle size={12} className="text-amber-500" />
                                        Business hours are Monday–Friday only. Weekends are not included in turnaround times.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Orders list */}
                        {loadingOrders ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="animate-spin text-teal-400" size={32} />
                            </div>
                        ) : userOrders.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <ClipboardList size={32} className="text-slate-600" />
                                </div>
                                <h3 className="text-xl font-semibold text-white mb-2">No orders yet</h3>
                                <p className="text-slate-500 mb-6">Upload a sketch to get started with your professional floor plan.</p>
                                <button
                                    onClick={onClose}
                                    className="px-6 py-3 bg-teal-600 hover:bg-teal-500 text-white rounded-xl font-medium transition-all"
                                >
                                    Upload a Sketch
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {userOrders.map((order) => {
                                    const isProcessing = order.status === 'pending' || order.status === 'in_progress';
                                    const isCompleted = order.status === 'completed';
                                    const estimation = isProcessing ? getEstimatedCompletion(order.created_at, order.turnaround) : null;
                                    
                                    return (
                                        <div
                                            key={order.id}
                                            className={`bg-slate-900 border rounded-2xl p-5 transition-all ${
                                                isProcessing ? 'border-amber-500/40 shadow-lg shadow-amber-900/10' : 
                                                isCompleted ? 'border-green-500/30' : 'border-slate-800'
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        {/* Status indicator */}
                                                        {isProcessing && (
                                                            <div className="relative">
                                                                <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                                                                    <svg 
                                                                        className="w-5 h-5 text-amber-400 animate-spin" 
                                                                        style={{ animationDuration: '2s' }}
                                                                        viewBox="0 0 24 24" 
                                                                        fill="none"
                                                                    >
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                                                    </svg>
                                                                </div>
                                                                <span className="absolute -bottom-1 -right-1 w-3 h-3 bg-amber-400 rounded-full animate-pulse" />
                                                            </div>
                                                        )}
                                                        {isCompleted && (
                                                            <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                                                                <CheckCircle size={20} className="text-green-400" />
                                                            </div>
                                                        )}
                                                        {order.status === 'cancelled' && (
                                                            <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
                                                                <X size={20} className="text-red-400" />
                                                            </div>
                                                        )}
                                                        
                                                        <div className="min-w-0">
                                                            <h4 className="font-semibold text-white truncate">{order.property_address}</h4>
                                                            <p className="text-sm text-slate-500">
                                                                {order.estimated_sq_ft?.toLocaleString() || '—'} sq ft • {order.property_type} • {order.floors} floor{order.floors > 1 ? 's' : ''}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Processing status */}
                                                    {isProcessing && estimation && (
                                                        <div className="mt-3 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                                                                        <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                                                                        <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                                                                    </div>
                                                                    <span className="text-amber-400 font-medium text-sm">
                                                                        {order.status === 'pending' ? 'Queued for processing' : 'Being drawn by our team'}
                                                                    </span>
                                                                </div>
                                                                <div className="text-right">
                                                                    <p className="text-xs text-slate-500">Est. completion</p>
                                                                    <p className={`font-semibold ${estimation.isOverdue ? 'text-green-400' : 'text-white'}`}>
                                                                        {estimation.text}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <p className="text-xs text-slate-500 mt-2">
                                                                {order.turnaround === 'express' ? '⚡ Express' : '📋 Standard'} turnaround • We'll email you when it's ready
                                                            </p>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Completed - download button(s) */}
                                                    {isCompleted && order.output_url && (() => {
                                                        const urls = getAllOutputUrls(order.output_url);
                                                        return (
                                                            <div className="mt-3">
                                                                <div className="flex flex-wrap gap-2">
                                                                    {urls.map((url, idx) => (
                                                                        <a
                                                                            key={idx}
                                                                            href={url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium text-sm transition-all"
                                                                        >
                                                                            <Download size={16} />
                                                                            {urls.length === 1 ? 'Download Floor Plan' : `Download ${idx + 1}`}
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                                {order.delivered_at && (
                                                                    <p className="text-xs text-slate-500 mt-2">
                                                                        Delivered on {new Date(order.delivered_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                                
                                                {/* Right side - date and credits */}
                                                <div className="text-right shrink-0">
                                                    <p className="text-xs text-slate-500">
                                                        {new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                                    </p>
                                                    <p className="text-sm text-slate-400 font-medium">
                                                        {order.payment_mode === 'direct_card' || order.payment_method === 'direct_card' || order.payment_method === 'card'
                                                          ? (order.amount_pence != null ? formatMoneyFromMinor(order.amount_pence, normalizeSketchplanCurrency(order.currency)) : 'Card')
                                                          : `${order.quoted_price} credits`}
                                                    </p>
                                                    <span className={`inline-block mt-2 text-xs font-medium px-2 py-1 rounded-full ${
                                                        isProcessing ? 'bg-amber-500/20 text-amber-400' :
                                                        isCompleted ? 'bg-green-500/20 text-green-400' :
                                                        'bg-red-500/20 text-red-400'
                                                    }`}>
                                                        {order.status === 'pending' && 'Queued'}
                                                        {order.status === 'in_progress' && 'In Progress'}
                                                        {order.status === 'completed' && 'Completed'}
                                                        {order.status === 'cancelled' && 'Cancelled'}
                                                    </span>
                                                </div>
                                            </div>
                                            <OrderWorkspace order={order} />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
                
                {/* CREDITS SECTION - ENSURE VISIBILITY */}
                {activeSection === 'credits' && (
                    <div className="max-w-5xl mx-auto animate-fade-in space-y-8">
                        {/* Sketchplan: buy exact credits (no discount) or use standard plans/top-ups for better rates */}
                        {currentApp === 'sketchplan' && (
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-slate-200 text-center">Buy only what you need</h3>
                                <div className="max-w-md mx-auto">
                                    <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 flex flex-col">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Zap className="text-teal-400" size={22} />
                                            <span className="font-bold text-white">{SKETCHPLAN_EXACT_CREDITS.name}</span>
                                        </div>
                                        <p className="text-sm text-slate-400 mb-4">{SKETCHPLAN_EXACT_CREDITS.description}</p>
                                        <div className="flex items-center gap-3 mb-4">
                                            <input
                                                type="number"
                                                min={minCustomCredits}
                                                max={maxCustomCredits}
                                                value={customCreditsAmount}
                                                onChange={(e) => {
                                                    const v = parseInt(e.target.value, 10);
                                                    if (!e.target.value.trim()) return;
                                                    setCustomCreditsAmount(isNaN(v) ? minCustomCredits : Math.min(maxCustomCredits, Math.max(minCustomCredits, v)));
                                                }}
                                                onBlur={() => setCustomCreditsAmount(c => (isNaN(c) || c < minCustomCredits ? minCustomCredits : Math.min(maxCustomCredits, c)))}
                                                className="w-24 bg-black border border-slate-600 rounded-lg px-3 py-2 text-white font-semibold text-center focus:outline-none focus:border-teal-500"
                                            />
                                            <span className="text-slate-400">credits</span>
                                        </div>
                                        <div className="text-slate-400 text-sm mb-6">
                                            {currency === 'GBP' ? '£' : '$'}{(currency === 'GBP' ? SKETCHPLAN_EXACT_CREDITS.prices.GBP.amountPerCredit : SKETCHPLAN_EXACT_CREDITS.prices.USD.amountPerCredit).toFixed(2)} per credit · Total: {currency === 'GBP' ? '£' : '$'}
                                            {(currency === 'GBP'
                                                ? customCreditsAmount * SKETCHPLAN_EXACT_CREDITS.prices.GBP.amountPerCredit
                                                : customCreditsAmount * SKETCHPLAN_EXACT_CREDITS.prices.USD.amountPerCredit
                                            ).toFixed(2)}
                                        </div>
                                        <button
                                            onClick={() => {
                                                const priceId = currency === 'GBP' ? SKETCHPLAN_EXACT_CREDITS.prices.GBP.id : SKETCHPLAN_EXACT_CREDITS.prices.USD.id;
                                                handleCheckout(customCreditsAmount, priceId, 'payment', customCreditsAmount);
                                            }}
                                            disabled={!!processingCheckout}
                                            className="w-full py-3 rounded-xl font-semibold bg-teal-600 hover:bg-teal-500 text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                        >
                                            {processingCheckout?.startsWith(currency === 'GBP' ? SKETCHPLAN_EXACT_CREDITS.prices.GBP.id : SKETCHPLAN_EXACT_CREDITS.prices.USD.id) ? (
                                                <Loader2 className="animate-spin" size={18} />
                                            ) : (
                                                `Buy ${customCreditsAmount} credits`
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <p className="text-center text-sm text-slate-500">Or get a better per-credit rate with a plan or top-up pack below.</p>
                            </div>
                        )}

                        {/* Currency and Billing Toggle */}
                        <div className="flex flex-col items-center gap-4">
                            {/* Currency Switcher */}
                            <div className="bg-slate-900/50 p-1 rounded-xl border border-slate-800 flex items-center gap-1">
                                <button
                                    onClick={() => setCurrency('USD')}
                                    className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${currency === 'USD' ? 'bg-slate-700 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                                >
                                    $ USD
                                </button>
                                <button
                                    onClick={() => setCurrency('GBP')}
                                    className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${currency === 'GBP' ? 'bg-slate-700 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                                >
                                    £ GBP
                                </button>
                            </div>
                            
                            {/* No subscriptions — top up credits as you need them */}
                            <div className="inline-flex items-center gap-2 rounded-full border border-teal-500/30 bg-teal-500/10 px-4 py-1.5 text-xs font-medium text-teal-300">
                                <Check size={13} />
                                No subscriptions — top up credits any time
                            </div>
                        </div>

                        {/* Products Grid */}
                        {displayProducts.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {displayProducts.map((product) => (
                                    <div key={product.id} className={`relative bg-slate-900 border rounded-2xl p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${product.popular ? 'border-teal-500/50 shadow-teal-900/10' : 'border-slate-800 hover:border-slate-700'}`}>
                                        {product.popular && (
                                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-teal-600 to-teal-400 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg tracking-wide uppercase">
                                                Most Popular
                                            </div>
                                        )}
                                        <div className="mb-4">
                                            <h3 className="text-lg font-bold text-white mb-1">{product.name}</h3>
                                            <div className="flex items-baseline gap-1">
                                                <span className="text-2xl font-bold text-slate-100">{product.currency === 'GBP' ? '£' : '$'}{product.displayAmount}</span>
                                                {billingTab === 'subscription' && <span className="text-xs text-slate-500">/mo</span>}
                                            </div>
                                        </div>

                                        <div className="flex-1 space-y-4 mb-8">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-teal-400 border border-slate-700">
                                                    <Zap size={20} className="fill-teal-400" />
                                                </div>
                                                <div>
                                                    <p className="font-bold text-slate-200 text-lg">{product.credits}</p>
                                                    <p className="text-xs text-slate-500 uppercase font-semibold">Credits</p>
                                                    {product.costPerCredit && (
                                                        <p className="text-[10px] text-slate-400 mt-0.5">
                                                            {product.costPerCredit}
                                                            {product.valueLabel && (
                                                                <span className="text-slate-400 ml-1">• {product.valueLabel}</span>
                                                            )}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <ul className="space-y-2">
                                                <li className="flex items-start gap-2 text-sm text-slate-400">
                                                    <Check size={16} className="text-green-500 shrink-0 mt-0.5" />
                                                    <span>High Speed Processing</span>
                                                </li>
                                                <li className="flex items-start gap-2 text-sm text-slate-400">
                                                    <Check size={16} className="text-green-500 shrink-0 mt-0.5" />
                                                    <span>Commercial Usage</span>
                                                </li>
                                                {billingTab === 'subscription' && product.thirdFeature && (
                                                    <li className="flex items-start gap-2 text-sm text-slate-400">
                                                    <Check size={16} className="text-green-500 shrink-0 mt-0.5" />
                                                    <span>{product.thirdFeature}</span>
                                                    </li>
                                                )}
                                            </ul>
                                        </div>

                                        <button
                                            onClick={() => handleCheckout(product.credits, product.priceId, billingTab === 'subscription' ? 'subscription' : 'payment')}
                                            disabled={processingCheckout === product.priceId}
                                            className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${product.popular ? 'bg-teal-600 hover:bg-teal-500 text-white shadow-lg shadow-teal-900/20' : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'}`}
                                        >
                                            {processingCheckout === product.priceId ? <Loader2 className="animate-spin" size={18} /> : (billingTab === 'subscription' ? 'Subscribe' : 'Buy Now')}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 border border-slate-800 rounded-xl bg-slate-900/50">
                                <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-500">
                                    <ShoppingBag size={24} />
                                </div>
                                <h3 className="text-lg font-medium text-slate-300">No products available</h3>
                                <p className="text-sm text-slate-500 max-w-sm mx-auto mt-2">
                                    We couldn't load the product catalog. Please check your connection or try again later.
                                </p>
                            </div>
                        )}
                        
                        {/* Error Display */}
                        {checkoutError && (
                             <div className="bg-red-900/20 border border-red-900/50 rounded-xl p-4 flex items-start gap-3 text-red-400 text-sm max-w-2xl mx-auto animate-shake">
                                 <ShieldAlert className="shrink-0 mt-0.5" size={18} />
                                 <div className="flex-1">
                                    <span className="font-semibold block mb-1">Checkout Failed</span>
                                    <span className="block opacity-90">{checkoutError}</span>
                                 </div>
                             </div>
                        )}
                        
                        {/* Promo Code Section */}
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md mx-auto">
                            <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                                <Sparkles size={16} className="text-teal-400" />
                                Have a promo code?
                            </h3>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={promoCode}
                                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                    placeholder="Enter code"
                                    className="flex-1 bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-teal-500 transition-colors uppercase tracking-wide"
                                    onKeyDown={(e) => e.key === 'Enter' && handleRedeemPromo()}
                                />
                                <button
                                    onClick={handleRedeemPromo}
                                    disabled={redeemingPromo || !promoCode.trim()}
                                    className="bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 disabled:text-slate-400 text-white font-semibold px-6 py-2.5 rounded-lg transition-all flex items-center gap-2 text-sm"
                                >
                                    {redeemingPromo ? <Loader2 size={16} className="animate-spin" /> : 'Redeem'}
                                </button>
                            </div>
                            {promoMessage && (
                                <div className={`mt-3 p-3 rounded-lg text-sm flex items-center gap-2 ${promoMessage.type === 'success' ? 'bg-green-900/30 border border-green-800 text-green-400' : 'bg-red-900/30 border border-red-800 text-red-400'}`}>
                                    {promoMessage.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                                    {promoMessage.text}
                                </div>
                            )}
                        </div>
                        
                        <div className="text-center text-xs text-slate-500 max-w-2xl mx-auto">
                             <p>Secure payment processing via Stripe. You can cancel subscriptions at any time from the settings menu.</p>
                        </div>
                    </div>
                )}

                {/* TRANSACTIONS */}
                {activeSection === 'transactions' && (
                  <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
                      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-lg">
                          <div className="p-6 border-b border-slate-800">
                              <div className="flex items-center justify-between mb-4">
                                  <h3 className="text-lg font-semibold text-white">Transaction History</h3>
                                  <span className="text-xs text-slate-500">Last 100 transactions</span>
                              </div>
                              
                              {/* Filter Tabs */}
                              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                                  <button
                                      onClick={() => setTransactionFilter('all')}
                                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                          transactionFilter === 'all' 
                                              ? 'bg-teal-600 text-white shadow-lg' 
                                              : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                                      }`}
                                  >
                                      All
                                  </button>
                                  <button
                                      onClick={() => setTransactionFilter('purchase')}
                                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                          transactionFilter === 'purchase' 
                                              ? 'bg-teal-600 text-white shadow-lg' 
                                              : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                                      }`}
                                  >
                                      Purchases
                                  </button>
                                  <button
                                      onClick={() => setTransactionFilter('usage')}
                                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                          transactionFilter === 'usage' 
                                              ? 'bg-teal-600 text-white shadow-lg' 
                                              : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                                      }`}
                                  >
                                      Usage
                                  </button>
                                  <button
                                      onClick={() => setTransactionFilter('refund')}
                                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                          transactionFilter === 'refund' 
                                              ? 'bg-teal-600 text-white shadow-lg' 
                                              : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                                      }`}
                                  >
                                      Refunds
                                  </button>
                                  <button
                                      onClick={() => setTransactionFilter('bonus')}
                                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                          transactionFilter === 'bonus' 
                                              ? 'bg-teal-600 text-white shadow-lg' 
                                              : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                                      }`}
                                  >
                                      Bonuses
                                  </button>
                              </div>
                          </div>
                          {loadingTransactions ? (
                              <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 text-teal-500 animate-spin" /></div>
                          ) : (() => {
                              const filteredTransactions = transactionFilter === 'all' 
                                  ? transactions 
                                  : transactions.filter(tx => tx.type === transactionFilter);
                              
                              return filteredTransactions.length > 0 ? (
                                  <div className="overflow-x-auto">
                                      <table className="w-full text-left border-collapse">
                                          <thead>
                                              <tr className="bg-black/50 border-b border-slate-800 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                  <th className="p-4 whitespace-nowrap">Date</th>
                                                  <th className="p-4 w-full">Description</th>
                                                  <th className="p-4 text-right whitespace-nowrap">Amount</th>
                                              </tr>
                                          </thead>
                                          <tbody className="divide-y divide-slate-800 text-sm text-slate-300">
                                              {filteredTransactions.map(tx => (
                                                  <tr key={tx.id} className="hover:bg-slate-800/50 transition-colors">
                                                      <td className="p-4 text-slate-400 whitespace-nowrap">
                                                          {new Date(tx.created_at).toLocaleDateString()} <span className="text-xs opacity-50 ml-1">{new Date(tx.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                      </td>
                                                      <td className="p-4 font-medium">{tx.description}</td>
                                                      <td className={`p-4 text-right font-bold whitespace-nowrap ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>{tx.amount > 0 ? '+' : ''}{tx.amount}</td>
                                                  </tr>
                                              ))}
                                          </tbody>
                                      </table>
                                  </div>
                              ) : (
                                  <div className="text-center py-16 px-6">
                                      <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-600"><Receipt size={32} /></div>
                                      <h4 className="text-slate-300 font-medium mb-1">No {transactionFilter === 'all' ? 'transactions' : transactionFilter + ' transactions'} yet</h4>
                                      <p className="text-sm text-slate-500">Your credit history will appear here once you start creating!</p>
                                  </div>
                              );
                          })()}
                      </div>
                  </div>
                )}

                {/* SETTINGS */}
                {activeSection === 'settings' && (
                    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                            <h3 className="text-lg font-semibold text-white mb-6">Account Details</h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-1">Email Address</label>
                                    <div className="flex items-center gap-3 bg-black border border-slate-800 rounded-xl px-4 py-3 text-slate-200"><Mail size={18} className="text-slate-500" />{user.email}</div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-1">Current Plan</label>
                                    <div className="flex items-center justify-between bg-black border border-slate-800 rounded-xl px-4 py-3">
                                        <div className="flex items-center gap-3 text-slate-200">
                                            <Package size={18} className={user.subscription_plan ? 'text-teal-400' : 'text-slate-500'} />
                                            <span>{user.subscription_plan || 'Free Tier'}</span>
                                        </div>
                                        {user.subscription_plan && user.stripe_customer_id ? (
                                            <button 
                                                onClick={handleManageSubscription}
                                                disabled={loadingPortal}
                                                className="text-xs bg-teal-600 hover:bg-teal-500 text-white px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50"
                                            >
                                                {loadingPortal ? 'Loading...' : 'Manage Subscription'}
                                            </button>
                                        ) : (
                                            <button onClick={() => setActiveSection('credits')} className="text-xs text-teal-400 hover:text-teal-300 font-medium">Upgrade</button>
                                        )}
                                    </div>
                                </div>
                                <div className="pt-4 border-t border-slate-800">
                                    <button onClick={onLogout} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600/10 hover:bg-red-600/20 text-red-400 hover:text-red-300 rounded-xl font-medium transition-colors border border-red-800/50">
                                        <LogOut size={18} /> Sign Out
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                             <h3 className="text-lg font-semibold text-white mb-4">Danger Zone</h3>
                             <p className="text-sm text-slate-400 mb-6">Once you delete your account, there is no going back. Please be certain.</p>
                             <button 
                                 onClick={() => setShowDeleteConfirm(true)}
                                 className="text-red-400 hover:text-red-300 hover:bg-red-900/10 px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-red-900/30"
                             >
                                 Delete Account
                             </button>
                        </div>
                    </div>
                )}
            </main>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            onClick={() => setLightboxImage(null)} 
            className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white bg-slate-800/50 rounded-full transition-colors z-10"
          >
            <X size={24} />
          </button>
          
          {/* Navigation arrows */}
          {(() => {
            const completedImages = userImages.filter(img => img.status === 'completed');
            const currentIndex = completedImages.findIndex(img => img.id === lightboxImage.id);
            const hasPrev = currentIndex > 0;
            const hasNext = currentIndex < completedImages.length - 1;
            
            return (
              <>
                {hasPrev && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); setLightboxImage(completedImages[currentIndex - 1]); setLightboxShowOriginal(false); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-slate-400 hover:text-white bg-slate-800/50 hover:bg-slate-700/50 rounded-full transition-colors z-10"
                  >
                    <ChevronLeft size={28} />
                  </button>
                )}
                {hasNext && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); setLightboxImage(completedImages[currentIndex + 1]); setLightboxShowOriginal(false); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-slate-400 hover:text-white bg-slate-800/50 hover:bg-slate-700/50 rounded-full transition-colors z-10"
                  >
                    <ChevronRight size={28} />
                  </button>
                )}
              </>
            );
          })()}
          
          <div 
            className="max-w-[90vw] max-h-[85vh] flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img 
                src={lightboxShowOriginal ? lightboxImage.input_url : lightboxImage.output_url} 
                alt={lightboxShowOriginal ? "Original" : "Restored"}
                className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
              />
              <div className="absolute top-3 left-3 bg-black/70 text-white text-xs font-bold px-3 py-1.5 rounded-full backdrop-blur-sm border border-white/10">
                {lightboxShowOriginal ? 'Original' : 'Restored'}
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setLightboxShowOriginal(false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${!lightboxShowOriginal ? 'bg-teal-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
              >
                Restored
              </button>
              <button
                onClick={() => setLightboxShowOriginal(true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${lightboxShowOriginal ? 'bg-teal-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
              >
                Original
              </button>
              <div className="w-px h-6 bg-slate-700" />
              <button
                onClick={() => handleDownload(lightboxShowOriginal ? lightboxImage.input_url : lightboxImage.output_url, lightboxImage.id)}
                disabled={downloadingId === lightboxImage.id}
                className="px-4 py-2 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white rounded-lg text-sm font-medium transition-all flex items-center gap-2"
              >
                {downloadingId === lightboxImage.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full animate-fade-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <ShieldAlert className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Delete Account</h3>
                <p className="text-sm text-slate-400">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-slate-300 mb-6">
              Are you sure you want to permanently delete your account? All your data, images, and remaining credits will be lost forever.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deletingAccount}
                className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {deletingAccount ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Deleting...</>
                ) : (
                  <><Trash2 className="w-4 h-4" /> Delete Forever</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
